import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn, formatCoordinate } from '@ais-ui/utils';
import { useMapContextMenu } from './MapContextMenuContext';
import { PortContextMenuItems } from './PortContextMenuItems';
import { ShapeContextMenuItems } from './ShapeContextMenuItems';
import { CoordinateContextMenuItems } from './CoordinateContextMenuItems';
import { EditShape } from '../../Shapes/Mutations/EditShape';
import { useSettings } from '../../Settings/SettingsProvider';

export const MapContextMenu = () => {
  const { state, closeContextMenu, editShapeDialog, closeEditShapeDialog } = useMapContextMenu();
  const { settings } = useSettings();
  const { isOpen, position, feature } = state;
  const menuRef = useRef<HTMLDivElement>(null);

  // Wrapper to match the expected Dispatch<SetStateAction<boolean>> type
  const handleSetShowDialog = useCallback((value: boolean | ((prev: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(editShapeDialog.isOpen) : value;
    if (!newValue) {
      closeEditShapeDialog();
    }
  }, [editShapeDialog.isOpen, closeEditShapeDialog]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeContextMenu();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeContextMenu();
      }
    };

    if (isOpen) {
      // Use a small delay to prevent immediate closing from the same click
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 10);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeContextMenu]);

  if (!isOpen || !position || !feature) {
    // Still render EditShape dialog even when context menu is closed
    return editShapeDialog.isOpen && editShapeDialog.shape ? (
      <EditShape 
        setShowDialog={handleSetShowDialog} 
        shape={editShapeDialog.shape} 
      />
    ) : null;
  }

  const getMenuLabel = () => {
    switch (feature.type) {
      case 'port':
        return feature.data.name;
      case 'vessel':
        return feature.data.name || `MMSI: ${feature.data.mmsi}`;
      case 'shape':
        return feature.data.properties?.name || 'Shape';
      case 'coordinate': {
        const format = settings['ribbonPlugin.settings']?.cursor_position_format;
        const { lat, lon } = formatCoordinate(
          feature.data.longitude,
          feature.data.latitude,
          format,
        );
        return `${lat} ${lon}`;
      }
      default:
        return 'Actions';
    }
  };

  const menuContent = (
    <div
      ref={menuRef}
      className={cn(
        'fixed z-50 min-w-[12rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md',
        'animate-in fade-in-0 zoom-in-95',
      )}
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      <div className="px-2 py-1.5 text-sm font-semibold">{getMenuLabel()}</div>
      <div className="-mx-1 my-1 h-px bg-border" />
      
      {feature.type === 'port' && (
        <PortContextMenuItems port={feature.data} />
      )}
      
      {feature.type === 'shape' && (
        <ShapeContextMenuItems shape={feature.data} />
      )}

      {feature.type === 'coordinate' && (
        <CoordinateContextMenuItems coordinate={feature.data} />
      )}
    </div>
  );

  return (
    <>
      {createPortal(menuContent, document.body)}
      {editShapeDialog.isOpen && editShapeDialog.shape && (
        <EditShape 
          setShowDialog={handleSetShowDialog} 
          shape={editShapeDialog.shape} 
        />
      )}
    </>
  );
};
